import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { ApiError } from './components/base-components/error-modal/error-modal.component';
import { stateGlobalDefault } from './models/onboarding-global-state.default';
import {
  AxaOptOutSuccess,
  CallAxaOptOut,
  CallDocumentUploadAPI,
  CallGenerateOtp,
  CallLogin,
  CallLogout,
  CallRegister,
  CallValidateOtp,
  CallValidateSession,
  DocumentUploadSuccess,
  ElevateSessionSuccess,
  LoginSuccess,
  ResetGlobalState,
  SendAnalyticsEvent,
  ToggleMenuOpen,
  UpdateAPIErrors
} from './onboarding.actions';
import { AnalyticsService } from './services/analytics.service';
import { MortgageService } from './modules/ukmortgage/services/mortgage.service';
import { OnboardingService } from './services/onboarding.service';
import { VoyService } from './modules/ukvoy/services/voy.service';

export interface OnboardingGlobalStateModel {
  menuOpen: boolean;
  hasAPIErrors: ApiError;
  documentUploadSuccess: boolean;
  axaOptOutUploadSuccess: boolean;
  isLoggedIn: boolean;
  isElevated: boolean;
  ssoUserId: string;
}

@State<OnboardingGlobalStateModel>({
  name: 'onbGlobalState',
  defaults: stateGlobalDefault
})
@Injectable()
export class OnboardingGlobalState {
  constructor(
    private onboardingService: OnboardingService,
    private mortgageService: MortgageService,
    private voyService: VoyService,
    private analyticsService: AnalyticsService
  ) {
  }

  @Action(ResetGlobalState)
  ResetGlobalState(ctx: StateContext<OnboardingGlobalStateModel>): void {
    ctx.patchState(stateGlobalDefault);
  }

  @Action(ToggleMenuOpen)
  ToggleMenuOpen(ctx: StateContext<OnboardingGlobalStateModel>): void {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      menuOpen: !state.menuOpen
    });
  }

  @Action(UpdateAPIErrors)
  UpdateAPIErrors(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const castedPayload: ApiError = payload; // typecast
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      hasAPIErrors: payload
    });
  }

  @Action(CallRegister)
  CallRegister(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const payloadCasted: { user: string; pass: string; otp: string } = payload;
    this.mortgageService.callRegister(payloadCasted.user, payloadCasted.pass, payloadCasted.otp);
  }

  @Action(CallLogin)
  CallLogin(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const payloadCasted: { user: string; pass: string } = payload;
    this.mortgageService.callLogin(payloadCasted.user, payloadCasted.pass);
  }

  @Action(CallLogout)
  CallLogout(ctx: StateContext<OnboardingGlobalStateModel>): void {
    this.mortgageService.logout();
  }

  @Action(CallGenerateOtp)
  CallGenerateOtp(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const payloadCasted: { user: string; resetPassword: boolean } = payload;
    this.mortgageService.callGenerateOtp(payloadCasted.user, payloadCasted.resetPassword);
  }

  @Action(CallValidateOtp)
  CallValidateOtp(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const payloadCasted: { user: string; otp: string } = payload;
    this.mortgageService.callValidateOtp(payloadCasted.user, payloadCasted.otp);
  }

  @Action(CallValidateSession)
  CallValidateSession(ctx: StateContext<OnboardingGlobalStateModel>): void {
    this.mortgageService.callValidateCookie();
  }

  @Action(LoginSuccess)
  LoginSuccess(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const payloadCasted: { success: boolean; userId: string } = payload;
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isElevated: false,
      isLoggedIn: payloadCasted.success,
      ssoUserId: payloadCasted.success ? payloadCasted.userId : null
    });
  }

  @Action(ElevateSessionSuccess)
  ElevateSessionSuccess(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const payloadCasted: boolean = payload;
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isElevated: payloadCasted
    });
  }

  @Action(CallDocumentUploadAPI)
  CallDocumentUploadAPI(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    this.onboardingService.callDocumentUploadAPI(payload);
  }

  @Action(DocumentUploadSuccess)
  DocumentUploadSuccess(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      documentUploadSuccess: payload
    });
  }

  @Action(SendAnalyticsEvent)
  SendAnalyticsEvent(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    this.analyticsService.sendAnalyticsEvent(payload);
  }

  @Action(CallAxaOptOut)
  CallAxaOptOut(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    this.voyService.callAxaOptOut(payload);
  }

  @Action(AxaOptOutSuccess)
  AxaOptOutSuccess(ctx: StateContext<OnboardingGlobalStateModel>, { payload }: any): void {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      axaOptOutUploadSuccess: payload
    });
  }
}
